import { useState } from 'react';
import api from '../../services/api';
import { btnPrimario, btnSecundario, campo, money, segmentos, segmento } from '../../components/ui/estilos';
import Dialogo from '../../components/ui/Dialogo';
import HojaPlatillo from './HojaPlatillo';
import { useCarrito } from './useCarrito';
import { TIEMPOS } from './mesas';

export type Destino =
  | { tipo: 'mesa'; mesaId: string; comandaId: string | null }
  | { tipo: 'llevar'; comandaId: string | null; cliente?: string };

const PROMOS = 'Promociones';
const mini: React.CSSProperties = { ...btnSecundario, width: 36, height: 36, padding: 0, borderRadius: 9, fontSize: 18 };

export default function TomaPedido({ titulo, destino, productos, promociones, onCerrar, onEnviado }: {
  titulo: string; destino: Destino; productos: any[]; promociones: any[];
  onCerrar: () => void; onEnviado: (msg: string) => void;
}) {
  const categorias = [...(promociones.length ? [PROMOS] : []), ...new Set(productos.map(p => p.categoria as string))];
  const [cat, setCat] = useState(categorias.find(c => c !== PROMOS) || categorias[0] || '');
  const [busca, setBusca] = useState('');
  const [menuTiempo, setMenuTiempo] = useState<string | null>(null);
  const [cliente, setCliente] = useState(destino.tipo === 'llevar' ? destino.cliente || '' : '');
  const [cobroTipo, setCobroTipo] = useState<'al_recoger' | 'inmediato'>('al_recoger');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  const c = useCarrito(promociones, productos);
  const llevarNuevo = destino.tipo === 'llevar' && !destino.comandaId;

  const q = busca.trim().toLowerCase();
  const visibles = q ? productos.filter(p => p.nombre.toLowerCase().includes(q))
    : cat === PROMOS ? [] : productos.filter(p => p.categoria === cat);

  const enviar = async () => {
    if (!c.lineas.length) return;
    if (llevarNuevo && !cliente.trim()) { setError('Escribe el nombre del cliente.'); return; }
    setEnviando(true); setError('');
    try {
      if (destino.comandaId) {
        await api.patch(`/comandas/${destino.comandaId}/items`, { agregar: c.items() });
      } else if (destino.tipo === 'mesa') {
        await api.post('/comandas', { mesa_id: destino.mesaId, items: c.items() });
      } else {
        await api.post('/comandas', { tipo: 'para_llevar', cliente_nombre: cliente.trim(), cobro_tipo: cobroTipo, items: c.items() });
      }
      c.vaciar();
      onEnviado('Pedido enviado a cocina.');
    } catch (e: any) {
      setError(e.response?.data?.error || 'No se pudo enviar el pedido. Intenta de nuevo.');
    } finally { setEnviando(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 15, background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
      <header style={{ minHeight: 68, flex: 'none', display: 'flex', alignItems: 'center', gap: 14, padding: '0 20px', background: 'var(--surface)', borderBottom: '1px solid var(--line)' }}>
        <button onClick={() => { if (!c.lineas.length || window.confirm('Hay platillos sin enviar a cocina. ¿Salir y descartarlos?')) onCerrar(); }} style={btnSecundario}>‹ Volver</button>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>{titulo} · {destino.comandaId ? 'Agregar al pedido' : 'Nuevo pedido'}</h1>
      </header>

      <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '200px minmax(0,1fr) 380px' }}>
        <nav aria-label="Categorías" style={{ borderRight: '1px solid var(--line)', background: 'var(--surface)', overflowY: 'auto', padding: '14px 10px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {categorias.map(k => {
            const on = k === cat && !q;
            return (
              <button key={k} onClick={() => { setCat(k); setBusca(''); }} aria-current={on || undefined} className="nav-item"
                style={{ minHeight: 52, textAlign: 'left', padding: '0 14px', border: 'none', borderRadius: 10, fontSize: 17, fontWeight: 700, cursor: 'pointer',
                  background: on ? 'var(--strong)' : 'transparent', color: on ? 'var(--on-strong)' : 'var(--ink)' }}>{k}</button>
            );
          })}
        </nav>

        <section style={{ overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar platillo…" aria-label="Buscar platillo" style={campo} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 14 }}>
            {cat === PROMOS && !q && promociones.map(p => (
              <button key={p.id} onClick={() => c.tocarPromo(p)} style={tarjetaPlatillo(false)}>
                <span style={{ fontSize: 17, fontWeight: 700 }}>{p.emoji} {p.nombre}</span>
                <span style={{ fontSize: 16, color: 'var(--ok)', fontWeight: 700 }}>{money(parseFloat(p.precio))}</span>
              </button>
            ))}
            {visibles.map(p => {
              const agotado = !p.disponible;
              return (
                <button key={p.id} onClick={() => !agotado && c.tocarProducto(p)} disabled={agotado} style={tarjetaPlatillo(agotado)}>
                  <span style={{ fontSize: 17, fontWeight: 700 }}>{p.nombre}</span>
                  <span style={{ fontSize: 16, color: agotado ? 'var(--danger)' : 'var(--muted)' }}>{agotado ? 'Agotado' : money(parseFloat(p.precio))}</span>
                </button>
              );
            })}
          </div>
          {q && !visibles.length && <p style={{ color: 'var(--muted)', fontSize: 16 }}>Sin resultados para “{busca}”.</p>}
        </section>

        <aside aria-label="Pedido" style={{ borderLeft: '1px solid var(--line)', background: 'var(--surface)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {llevarNuevo && (
            <div style={{ padding: '16px 20px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input autoFocus value={cliente} onChange={e => setCliente(e.target.value)} placeholder="Nombre del cliente" maxLength={120} style={campo} />
              <div style={segmentos}>
                {([['al_recoger', 'Paga al recoger'], ['inmediato', 'Prepago']] as const).map(([k, l]) => (
                  <button key={k} onClick={() => setCobroTipo(k)} aria-pressed={cobroTipo === k} style={{ ...segmento(cobroTipo === k), flex: 1, justifyContent: 'center' }}>{l}</button>
                ))}
              </div>
            </div>
          )}
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '18px 20px' }}>
            {!c.lineas.length && <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--muted)', fontSize: 16 }}>Toca un platillo para agregarlo.</div>}
            {c.lineas.map(l => (
              <div key={l.key} style={{ padding: '12px 0', borderBottom: '1px solid var(--line-2)', display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 16, fontWeight: 700 }}>
                  <span>{l.promo_id ? `${l.promo_emoji || ''} ` : ''}{l.nombre}</span><span>{money(l.precio * l.cantidad)}</span>
                </div>
                {l.modificadores.length > 0 && <div style={{ fontSize: 14, color: 'var(--muted)' }}>{l.modificadores.map(m => m.nombre).join(' · ')}</div>}
                {l.notas && <div style={{ fontSize: 14, color: 'var(--muted)' }}>Nota: {l.notas}</div>}
                {l.promo_nombre && <div style={{ fontSize: 14, color: 'var(--ok)', fontWeight: 700 }}>{l.promo_nombre}</div>}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ position: 'relative' }}>
                    <button onClick={() => setMenuTiempo(menuTiempo === l.key ? null : l.key)} aria-expanded={menuTiempo === l.key}
                      style={{ fontSize: 13, fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: 'var(--well)', border: 'none', cursor: 'pointer', color: 'var(--ink)' }}>
                      {TIEMPOS[l.tiempo]} ▾
                    </button>
                    {menuTiempo === l.key && (
                      <div style={{ position: 'absolute', left: 0, top: 32, zIndex: 2, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, boxShadow: '0 8px 24px rgba(40,30,20,.15)', padding: 4, display: 'flex', flexDirection: 'column', minWidth: 130 }}>
                        {Object.entries(TIEMPOS).map(([t, label]) => (
                          <button key={t} onClick={() => { c.tiempo(l.key, +t); setMenuTiempo(null); }}
                            style={{ height: 40, padding: '0 12px', border: 'none', borderRadius: 8, textAlign: 'left', fontSize: 14, fontWeight: 700, cursor: 'pointer', background: l.tiempo === +t ? 'var(--well)' : 'transparent', color: 'var(--ink)' }}>{label}</button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ flex: 1 }} />
                  {!l.promo_id && <>
                    <button onClick={() => c.cantidad(l.key, -1)} aria-label={`Menos ${l.nombre}`} style={mini}>−</button>
                    <span style={{ minWidth: 20, textAlign: 'center', fontSize: 16, fontWeight: 700 }}>{l.cantidad}</span>
                    <button onClick={() => c.cantidad(l.key, 1)} aria-label={`Más ${l.nombre}`} style={mini}>+</button>
                  </>}
                  <button onClick={() => c.quitar(l.key)} aria-label={`Quitar ${l.nombre}`} style={{ ...mini, fontSize: 16 }}>✕</button>
                </div>
              </div>
            ))}
          </div>
          <div style={{ flex: 'none', padding: '16px 20px 20px', borderTop: '1px solid var(--line)', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {error && <div role="alert" style={{ color: 'var(--danger)', fontWeight: 700 }}>{error}</div>}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 20, fontWeight: 700 }}><span>Total nuevo</span><span>{money(c.total)}</span></div>
            <button onClick={enviar} disabled={!c.lineas.length || enviando}
              style={{ ...btnPrimario, opacity: c.lineas.length && !enviando ? 1 : 0.4, cursor: c.lineas.length ? 'pointer' : 'not-allowed' }}>
              {enviando ? 'Enviando…' : `Enviar a cocina (${c.nItems})`}
            </button>
          </div>
        </aside>
      </div>

      {c.hoja && <HojaPlatillo hoja={c.hoja} tiempoInicial={1} onConfirmar={c.confirmarHoja} onCancelar={c.cancelarHoja} />}

      {c.sugerencia && (
        <Dialogo titulo={`${c.sugerencia.emoji || ''} ${c.sugerencia.nombre}`} onCerrar={c.rechazarSugerencia}>
          <p style={{ margin: 0, fontSize: 16, lineHeight: 1.45 }}>
            Lo que llevas cubre esta promoción por <b>{money(parseFloat(c.sugerencia.precio))}</b>. ¿La aplicamos?
          </p>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={c.rechazarSugerencia} style={{ ...btnSecundario, flex: 1, height: 56 }}>No, dejar separado</button>
            <button onClick={c.aceptarSugerencia} style={{ ...btnPrimario, flex: 1 }}>Aplicar promoción</button>
          </div>
        </Dialogo>
      )}
    </div>
  );
}

function tarjetaPlatillo(agotado: boolean): React.CSSProperties {
  return {
    textAlign: 'left', minHeight: 96, padding: 16, borderRadius: 14, border: '1px solid var(--line)', background: 'var(--surface)',
    cursor: agotado ? 'not-allowed' : 'pointer', display: 'flex', flexDirection: 'column', gap: 8, color: 'var(--ink)', opacity: agotado ? 0.5 : 1,
  };
}
